import { useCallback, useEffect, useState } from "react";

import {
  clearLlmLogs,
  listLlmLogs,
  readLlmLog,
  type LlmLogEntry,
} from "@/lib/llmLogs";
import { useDialogs } from "@/components/shell/Dialogs";

import { Panel, PanelButton } from "./Setup";

/**
 * Model calls — what was actually sent, and what came back.
 *
 * **This is the answer to "why did it say that".** Everything else about an LLM surface is
 * inference: the transcript shows what the reader saw, the settings show what was configured, and
 * neither can tell you what the model was given. The envelope in particular is rendered fresh onto
 * each question and deliberately never persisted into the thread, so this table is the *only* place
 * an exact prompt can be recovered rather than plausibly reconstructed.
 *
 * Three columns earn their place over "it worked":
 *
 * - **`cached`** — the number the whole caching design rests on. Zero down the column means every
 *   round is being paid for in full, and nothing else on any screen would say so.
 * - **`round`** — a tool-calling question is up to six requests, and the interesting failures are
 *   in the difference between them. Rows sharing a correlation are one question.
 * - **`ms`** — a reasoning model that quietly turned its thinking back on shows up here as six
 *   seconds before the first token, and nowhere else.
 *
 * Rows load lazily and the body of one is fetched only when it is opened: a page that selected the
 * prompts would pull megabytes to draw a list of times.
 */
export function LogsPanel({ onError }: { onError: (error: unknown) => void }) {
  const [entries, setEntries] = useState<LlmLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [surface, setSurface] = useState<string>("");
  const [open, setOpen] = useState<LlmLogEntry | null>(null);
  const [loaded, setLoaded] = useState(false);
  const dialogs = useDialogs();

  const load = useCallback(
    (which: string) => {
      void listLlmLogs({ limit: 50, surface: which || undefined })
        .then((page) => {
          setEntries(page.entries);
          setTotal(page.total);
          setLoaded(true);
        })
        .catch(onError);
    },
    [onError],
  );

  useEffect(() => load(surface), [load, surface]);

  const clear = async () => {
    // Gated, and the count is in the question. This is the one action here that destroys anything,
    // and nothing else prunes the table — so the reader is deciding to forget, not tidying up.
    const confirmed = await dialogs.confirm({
      title: `記録したモデル呼び出しをすべて削除？`,
      body: "取り消せません。この表を整理する仕組みは他にありません。",
      detail: `${total.toLocaleString()}件`,
      confirmLabel: "すべて削除",
      tone: "danger",
    });
    if (!confirmed) return;
    try {
      await clearLlmLogs();
      load(surface);
      setOpen(null);
    } catch (e) {
      onError(e);
    }
  };

  return (
    <Panel
      title="モデル呼び出し"
      subtitle={loaded ? `${total.toLocaleString()}件` : "読み込み中…"}
      actions={
        <>
          <PanelButton onClick={() => load(surface)}>更新</PanelButton>
          <PanelButton
            tone="danger"
            check="setup.logs.clear"
            onClick={() => void clear()}
            disabled={total === 0}
          >
            消去
          </PanelButton>
        </>
      }
    >
      <div className="flex flex-wrap items-center gap-2 border-b border-line-soft px-[18px] py-[10px]">
        {[
          { value: "", label: "すべて" },
          { value: "ask", label: "質問" },
          { value: "translate", label: "翻訳" },
        ].map(({ value, label }) => (
          <button
            key={value || "all"}
            type="button"
            onClick={() => setSurface(value)}
            className={`rounded-[var(--r-2)] border px-[9px] py-[4px] text-[14px]/[1] ${
              surface === value
                ? "border-accent-border bg-accent-tint text-accent-text"
                : "border-line-strong bg-void text-ink-3"
            }`}
          >
            {label}
          </button>
        ))}
        <span className="flex-1" />
        <span className="text-meta text-ink-4">
          自動では整理されません。古い呼び出しほど、後から探すことになるからです。
        </span>
      </div>

      {loaded && entries.length === 0 && (
        <p className="px-[18px] py-3 text-secondary text-ink-3">
          まだ呼び出しの記録はありません。
        </p>
      )}

      {entries.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[14px]/[1.4]">
            <thead className="bg-sunken">
              <tr className="text-ink-3">
                {["時刻", "surface", "モデル", "回", "入力", "キャッシュ", "出力", "ミリ秒", ""].map((h) => (
                  <th key={h} className="px-[10px] py-[6px] text-left font-semibold">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id} className="border-b border-line-soft last:border-b-0">
                  <td className="whitespace-nowrap px-[10px] py-[6px] font-mono text-ink-3">
                    {when(entry.createdAt)}
                  </td>
                  <td className="px-[10px] py-[6px] text-ink-3">{entry.surface}</td>
                  <td className="max-w-[220px] truncate px-[10px] py-[6px] font-mono text-ink-3">
                    {entry.model}
                  </td>
                  <td className="px-[10px] py-[6px] text-ink-4">{entry.round + 1}</td>
                  <td className="px-[10px] py-[6px] font-mono text-ink-3">
                    {entry.promptTokens.toLocaleString()}
                  </td>
                  {/* Muted at zero rather than hidden: a blank would read as "not measured", and
                      the whole point of the column is that a run of zeros is a finding. */}
                  <td
                    className={`px-[10px] py-[6px] font-mono ${
                      entry.cachedTokens > 0 ? "text-ink-3" : "text-ink-4"
                    }`}
                  >
                    {entry.cachedTokens.toLocaleString()}
                  </td>
                  <td className="px-[10px] py-[6px] font-mono text-ink-3">
                    {entry.completionTokens.toLocaleString()}
                  </td>
                  <td className="px-[10px] py-[6px] font-mono text-ink-4">{entry.durationMs}</td>
                  <td className="px-[10px] py-[6px]">
                    <div className="flex items-center gap-[6px]">
                      {entry.error && (
                        <span className="text-meta text-[color:var(--ink-3)]" title={entry.error}>
                          失敗
                        </span>
                      )}
                      {entry.hasReasoning && <span className="text-meta text-ink-4">思考</span>}
                      <button
                        type="button"
                        onClick={() => {
                          void readLlmLog(entry.id).then(setOpen).catch(onError);
                        }}
                        className="font-mono text-meta text-ink-4 hover:text-ink-2"
                      >
                        開く ›
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && <Detail entry={open} onClose={() => setOpen(null)} />}
    </Panel>
  );
}

/** One call in full. Plain `<pre>` throughout — this is a diagnostic, and rendering the prompt as
 *  anything other than the exact characters that were sent would defeat it. */
function Detail({ entry, onClose }: { entry: LlmLogEntry; onClose: () => void }) {
  return (
    <div className="flex flex-col gap-3 border-t border-line-soft bg-sunken px-[18px] py-3">
      <div className="flex flex-wrap items-center gap-[10px]">
        <span className="font-jp text-eyebrow font-semibold uppercase tracking-[.1em] text-ink-3">
          {entry.surface} · ラウンド{entry.round + 1}
        </span>
        <span className="font-mono text-meta text-ink-4">{entry.model}</span>
        {entry.correlation && (
          <span className="font-mono text-meta text-ink-4" title="1つの質問の各ラウンドが共有します">
            {entry.correlation.slice(0, 8)}
          </span>
        )}
        <span className="flex-1" />
        <PanelButton onClick={onClose}>閉じる</PanelButton>
      </div>

      {entry.error && (
        <Block label="エラー" tone="gap">
          {entry.error}
        </Block>
      )}
      {entry.reasoning && <Block label="思考">{entry.reasoning}</Block>}
      {entry.response && <Block label="応答">{entry.response}</Block>}
      {entry.toolCalls.length > 0 && (
        <Block label="ツール呼び出し">{JSON.stringify(entry.toolCalls, null, 2)}</Block>
      )}
      <Block label="プロンプト（送信そのまま）">{JSON.stringify(entry.request, null, 2)}</Block>
    </div>
  );
}

function Block({
  label,
  tone,
  children,
}: {
  label: string;
  tone?: "gap";
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-meta text-ink-4">{label}</span>
      <pre
        className={`max-h-[320px] overflow-auto whitespace-pre-wrap break-words rounded-[var(--r-2)] border border-line bg-void p-2 font-mono text-[14px]/[1.5] ${
          tone === "gap" ? "text-[color:var(--ink-3)]" : "text-ink-2"
        }`}
      >
        {children}
      </pre>
    </div>
  );
}

function when(seconds: number): string {
  const date = new Date(seconds * 1000);
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}
